import { useEffect, useState } from "react";
import { X, Smartphone, ChevronRight, Download, MoreVertical } from "lucide-react";
import {
  getInstallState,
  triggerNativeInstall,
  subscribeInstallState,
} from "../lib/installPrompt";

const STORAGE_KEY = "install_guide_seen_v1";

export function shouldShowInstallGuide() {
  try {
    if (localStorage.getItem(STORAGE_KEY)) return false;
  } catch {
    return false;
  }
  return canShowInstallGuide();
}

export function canShowInstallGuide() {
  return getInstallState().canInstall;
}

export function markInstallGuideSeen() {
  try {
    localStorage.setItem(STORAGE_KEY, "1");
  } catch {}
}

export default function InstallGuideModal({ open, onClose }) {
  const [index, setIndex] = useState(0);
  const [state, setState] = useState(getInstallState);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    setState(getInstallState());
    return subscribeInstallState(() => setState(getInstallState()));
  }, [open]);

  if (!open) return null;

  const close = () => {
    setIndex(0);
    onClose?.();
  };

  const handleAndroidInstall = async () => {
    setInstalling(true);
    const res = await triggerNativeInstall();
    setInstalling(false);
    if (res.success) close();
  };

  // Decide content based on platform
  let body;
  if (state.kind === "android" && state.hasNativePrompt) {
    body = <AndroidNativeBody onInstall={handleAndroidInstall} installing={installing} />;
  } else if (state.kind === "android-manual") {
    body = <AndroidManualBody />;
  } else {
    body = <IOSCarouselBody index={index} setIndex={setIndex} onDone={close} />;
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(14, 34, 64, 0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 18,
        zIndex: 100,
      }}
      data-testid="install-guide-modal"
    >
      <div
        style={{
          width: "100%",
          maxWidth: 420,
          maxHeight: "92vh",
          background: "var(--surface)",
          borderRadius: 28,
          border: "1px solid var(--border)",
          overflow: "hidden",
          paddingBottom: 18,
          position: "relative",
        }}
      >
        <button
          onClick={close}
          aria-label="Close"
          data-testid="install-guide-skip"
          style={{
            position: "absolute",
            top: 12, right: 12,
            width: 36, height: 36,
            borderRadius: 18,
            background: "var(--surface-soft)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 5,
          }}
        >
          <X size={20} color="var(--text-secondary)" />
        </button>

        <div style={{ padding: "20px 22px 4px", textAlign: "center" }}>
          <div style={{
            width: 40, height: 40, borderRadius: 12, background: "var(--primary)",
            display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 8,
          }}>
            <Smartphone size={20} color="#FFFFFF" />
          </div>
          <h2 style={{ color: "var(--text-primary)", fontSize: 18, fontWeight: 800, letterSpacing: -0.4, margin: 0 }}>
            Add as an App
          </h2>
          <p style={{ color: "var(--text-secondary)", fontSize: 12, marginTop: 4 }}>
            Get one-tap access right from your home screen.
          </p>
        </div>

        {body}
      </div>
    </div>
  );
}

/* ---------- Platform-specific bodies ---------- */

function IOSCarouselBody({ index, setIndex, onDone }) {
  const SLIDES = [
    {
      step: "Step 1",
      title: "Tap the three dots",
      body: "In Safari, tap the three small dots in the bottom-right of the address bar.",
      image: "/install/step1.jpg",
    },
    {
      step: "Step 2",
      title: "Tap Share",
      body: "From the menu that opens, tap 'Share'.",
      image: "/install/step2.jpg",
    },
    {
      step: "Step 3",
      title: "Tap Add to Home Screen",
      body: "Scroll the share sheet and tap 'Add to Home Screen'.",
      image: "/install/step3.jpg",
    },
  ];
  const slide = SLIDES[index];
  const isLast = index === SLIDES.length - 1;
  return (
    <>
      <div style={{ padding: "0 18px", marginTop: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 4 }}>
          <span style={{
            background: "var(--primary)", color: "#FFFFFF",
            fontWeight: 800, fontSize: 11, letterSpacing: 0.6,
            padding: "4px 10px", borderRadius: 999,
          }}>
            {slide.step}
          </span>
          <span style={{ color: "var(--text-primary)", fontSize: 15, fontWeight: 800, flex: 1 }}>
            {slide.title}
          </span>
        </div>
        <p style={{ color: "var(--text-secondary)", fontSize: 12.5, marginTop: 6, marginBottom: 8, lineHeight: 1.4 }}>
          {slide.body}
        </p>

        <div
          data-testid="install-guide-image-box"
          style={{
            position: "relative",
            width: "100%",
            height: "min(46vh, 360px)",
            background: "#FDF1E6",
            borderRadius: 18,
            border: "1px solid var(--peach-border)",
            overflow: "hidden",
          }}
        >
          {SLIDES.map((s, i) => (
            <img
              key={i}
              src={s.image}
              alt={s.title}
              data-testid={`install-guide-image-${i}`}
              aria-hidden={i !== index}
              style={{
                position: "absolute",
                inset: 0,
                margin: "auto",
                maxWidth: "100%",
                maxHeight: "100%",
                width: "auto",
                height: "100%",
                objectFit: "contain",
                display: "block",
                opacity: i === index ? 1 : 0,
                transition: "opacity 120ms ease",
                pointerEvents: "none",
              }}
            />
          ))}
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 12, marginBottom: 12 }}>
        {SLIDES.map((_, i) => (
          <div key={i} style={{
            width: i === index ? 22 : 8, height: 8, borderRadius: 4,
            background: i === index ? "var(--primary)" : "var(--border)",
            transition: "all .25s ease",
          }} />
        ))}
      </div>

      <div style={{ display: "flex", gap: 10, padding: "0 18px" }}>
        {index > 0 ? (
          <button className="btn-ghost" onClick={() => setIndex(index - 1)} data-testid="install-guide-back">
            Back
          </button>
        ) : (
          <button className="btn-ghost" onClick={onDone} data-testid="install-guide-skip-text">
            Skip
          </button>
        )}
        <button
          className="btn-primary"
          style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}
          onClick={() => (isLast ? onDone() : setIndex(index + 1))}
          data-testid="install-guide-next"
        >
          {isLast ? "Got it" : "Next"}
          {!isLast && <ChevronRight size={18} />}
        </button>
      </div>
    </>
  );
}

function AndroidNativeBody({ onInstall, installing }) {
  return (
    <>
      <div style={{ padding: "0 22px 0", textAlign: "center" }}>
        <p style={{ color: "var(--text-secondary)", fontSize: 14, lineHeight: 1.5, margin: "8px 0 18px" }}>
          One tap to install Cashback on your phone — no app store needed.
        </p>
        <div style={{
          background: "var(--surface-elev)", border: "1px solid var(--peach-border)",
          borderRadius: 22, padding: 22, display: "flex", flexDirection: "column", alignItems: "center", gap: 10,
        }}>
          <div style={{
            width: 84, height: 84, borderRadius: 22, background: "var(--navy)",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 16px 32px -16px rgba(14,34,64,0.4)",
          }}>
            <Smartphone size={38} color="#FFFFFF" />
          </div>
          <div style={{ fontSize: 16, fontWeight: 800, color: "var(--text-primary)" }}>Cashback</div>
          <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>
            {typeof window !== "undefined" ? window.location.hostname : ""}
          </div>
        </div>
      </div>
      <div style={{ padding: "16px 18px 0" }}>
        <button
          className="btn-primary"
          onClick={onInstall}
          disabled={installing}
          data-testid="install-guide-android-install"
          style={{
            width: "100%", display: "inline-flex", alignItems: "center", justifyContent: "center",
            gap: 8, opacity: installing ? 0.7 : 1,
          }}
        >
          <Download size={18} />
          {installing ? "Installing…" : "Install app"}
        </button>
      </div>
    </>
  );
}

function AndroidManualBody() {
  return (
    <div style={{ padding: "0 22px 6px" }}>
      <p style={{ color: "var(--text-secondary)", fontSize: 14, lineHeight: 1.5, marginTop: 4 }}>
        Add Cashback to your phone in two taps:
      </p>
      <div style={{
        background: "var(--surface-elev)", border: "1px solid var(--border)",
        borderRadius: 18, padding: 16, marginTop: 12,
      }}>
        <Step n="1" icon={<MoreVertical size={18} color="var(--primary)" />}>
          Tap the <b>menu icon</b> (⋮) in the top-right of Chrome.
        </Step>
        <Step n="2" icon={<Download size={18} color="var(--primary)" />} last>
          Tap <b>"Install app"</b> or <b>"Add to Home screen"</b>.
        </Step>
      </div>
      <p style={{ color: "var(--text-muted)", fontSize: 12, lineHeight: 1.45, marginTop: 12 }}>
        Don't see it? Make sure you're on the latest Chrome. The app will then live on your phone like any other app.
      </p>
    </div>
  );
}

function Step({ n, icon, children, last }) {
  return (
    <div style={{
      display: "flex", alignItems: "flex-start", gap: 12,
      paddingBottom: last ? 0 : 14, marginBottom: last ? 0 : 14,
      borderBottom: last ? "none" : "1px solid var(--border)",
    }}>
      <span style={{
        width: 28, height: 28, borderRadius: 999, background: "var(--primary)",
        color: "#FFFFFF", fontWeight: 800, fontSize: 13,
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}>{n}</span>
      <div style={{ flex: 1, color: "var(--text-primary)", fontSize: 14, lineHeight: 1.45 }}>
        {children}
        <div style={{ marginTop: 6 }}>{icon}</div>
      </div>
    </div>
  );
}
